// src/context/NotificationContext.js
import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { useI18n } from '../i18n/i18n';

const NotificationContext = createContext();

// Типи сповіщень
const NOTIFICATION_TYPES = {
  EFFICIENCY_ALERT: 'efficiency_alert',
  RESOURCE_ALERT: 'resource_alert',
  PRODUCTION_TARGET: 'production_target',
  MAINTENANCE_REMINDER: 'maintenance_reminder',
  ANOMALY_DETECTED: 'anomaly_detected',
  ML_MODEL_UPDATED: 'ml_model_updated',
  SHIFT_OPTIMIZATION: 'shift_optimization'
};

// Пріоритети
const PRIORITY_LEVELS = {
  CRITICAL: 'critical',
  HIGH: 'high',
  MEDIUM: 'medium',
  LOW: 'low',
  INFO: 'info'
};

// Початковий стан
const initialState = {
  notifications: [],
  unreadCount: 0,
  settings: {
    emailNotifications: true,
    pushNotifications: true,
    efficiencyAlerts: true,
    resourceAlerts: true,
    anomalyAlerts: true,
    maintenanceAlerts: true,
    achievementAlerts: true,
    highPriorityOnly: false
  },
  channels: {
    email: {
      enabled: true,
      recipients: [],
      lastSent: null
    },
    push: {
      enabled: true,
      subscribers: [],
      lastSent: null
    }
  }
};

// Редуктор
function notificationReducer(state, action) {
  switch (action.type) {
    case 'ADD_NOTIFICATION':
      const newNotification = {
        id: Date.now().toString(),
        timestamp: new Date(),
        read: false,
        ...action.payload
      };
      
      return {
        ...state,
        notifications: [newNotification, ...state.notifications].slice(0, 100), // Ліміт 100 сповіщень
        unreadCount: state.unreadCount + 1
      };

    case 'MARK_AS_READ':
      return {
        ...state,
        notifications: state.notifications.map(notif =>
          notif.id === action.payload ? { ...notif, read: true } : notif
        ),
        unreadCount: Math.max(0, state.unreadCount - 1)
      };

    case 'MARK_ALL_READ':
      return {
        ...state,
        notifications: state.notifications.map(notif => ({ ...notif, read: true })),
        unreadCount: 0
      };

    case 'DELETE_NOTIFICATION':
      const notificationToDelete = state.notifications.find(n => n.id === action.payload);
      return {
        ...state,
        notifications: state.notifications.filter(notif => notif.id !== action.payload),
        unreadCount: notificationToDelete && !notificationToDelete.read ? state.unreadCount - 1 : state.unreadCount
      };

    case 'UPDATE_SETTINGS':
      return {
        ...state,
        settings: { ...state.settings, ...action.payload }
      };

    case 'UPDATE_CHANNELS':
      return {
        ...state,
        channels: { ...state.channels, ...action.payload }
      };

    case 'CLEAR_ALL':
      return {
        ...state,
        notifications: [],
        unreadCount: 0
      };

    default:
      return state;
  }
}

// Хук для використання сповіщень
export function useNotifications() {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
}

// ТИМЧАСОВИЙ ХУК ДЛЯ ЗВОРОТНОЇ СУМІСНОСТІ
// Видаліть цей хук після оновлення всіх компонентів
export function useNotification() {
  console.warn('useNotification is deprecated. Please update to useNotifications.');
  return useNotifications();
}

// Постачальник контексту
export function NotificationProvider({ children }) {
  const [state, dispatch] = useReducer(notificationReducer, initialState);
  const { t } = useI18n();

  // Дії
  const actions = {
    addNotification: (notification) => {
      dispatch({ type: 'ADD_NOTIFICATION', payload: notification });
      
      // Автоматичне відправлення через канали (якщо активовано)
      if (state.settings.pushNotifications && notification.priority !== PRIORITY_LEVELS.LOW) {
        sendPushNotification(notification, t);
      }
      
      if (state.settings.emailNotifications && notification.priority === PRIORITY_LEVELS.CRITICAL) {
        sendEmailNotification(notification, state.channels.email.recipients, t);
      }
    },

    // ✅ ДОДАНО: Функція showNotification для зворотної сумісності
    showNotification: (title, message, type = 'info', priority = 'info') => {
      const notification = {
        title,
        message,
        type: type || NOTIFICATION_TYPES.EFFICIENCY_ALERT,
        priority: priority || PRIORITY_LEVELS.INFO
      };
      dispatch({ type: 'ADD_NOTIFICATION', payload: notification });
      
      // Автоматичне відправлення через канали
      if (state.settings.pushNotifications && priority !== PRIORITY_LEVELS.LOW) {
        sendPushNotification(notification, t);
      }
    },

    markAsRead: (id) => dispatch({ type: 'MARK_AS_READ', payload: id }),
    markAllRead: () => dispatch({ type: 'MARK_ALL_READ' }),
    deleteNotification: (id) => dispatch({ type: 'DELETE_NOTIFICATION', payload: id }),
    updateSettings: (settings) => dispatch({ type: 'UPDATE_SETTINGS', payload: settings }),
    updateChannels: (channels) => dispatch({ type: 'UPDATE_CHANNELS', payload: channels }),
    clearAll: () => dispatch({ type: 'CLEAR_ALL' }),

    // Спеціалізовані методи для різних типів сповіщень
    notifyEfficiencyAlert: (data) => {
      const notification = createEfficiencyNotification(data, t);
      actions.addNotification(notification);
    },

    notifyResourceAlert: (data) => {
      const notification = createResourceNotification(data, t);
      actions.addNotification(notification);
    },

    notifyProductionTarget: (data) => {
      const notification = createProductionTargetNotification(data, t);
      actions.addNotification(notification);
    },

    notifyMaintenance: (data) => {
      const notification = createMaintenanceNotification(data, t);
      actions.addNotification(notification);
    },

    notifyAnomaly: (data) => {
      const notification = createAnomalyNotification(data, t);
      actions.addNotification(notification);
    },

    // ✅ ДОДАНО: Функції для AI-рекомендацій
    notifyAIRecommendation: (recommendation) => {
      const notification = {
        type: NOTIFICATION_TYPES.EFFICIENCY_ALERT,
        priority: recommendation.priority || PRIORITY_LEVELS.MEDIUM,
        title: recommendation.title || t('ai_recommendations_title'),
        message: recommendation.message || recommendation.description,
        data: recommendation,
        category: 'ai_recommendation'
      };
      actions.addNotification(notification);
    },

    notifyAnomalyDetection: (anomaly) => {
      const notification = {
        type: NOTIFICATION_TYPES.ANOMALY_DETECTED,
        priority: anomaly.severity === 'critical' ? PRIORITY_LEVELS.CRITICAL : PRIORITY_LEVELS.HIGH,
        title: t('alert_anomaly_detected'),
        message: `${anomaly.type}: ${anomaly.description}`,
        data: anomaly,
        category: 'anomaly'
      };
      actions.addNotification(notification);
    }
  };

  // Запит дозволу на сповіщення при завантаженні
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);

  const value = {
    ...state,
    ...actions,
    NOTIFICATION_TYPES,
    PRIORITY_LEVELS
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
}

// Допоміжні функції для створення сповіщень
function createEfficiencyNotification(data, t) {
  return {
    type: NOTIFICATION_TYPES.EFFICIENCY_ALERT,
    priority: data.efficiency < 50 ? PRIORITY_LEVELS.CRITICAL : 
              data.efficiency < 70 ? PRIORITY_LEVELS.HIGH : PRIORITY_LEVELS.MEDIUM,
    title: t('alert_low_efficiency'),
    message: t('efficiency_alert_message', { 
      efficiency: Math.round(data.efficiency),
      shift: data.shift,
      date: new Date(data.date).toLocaleDateString()
    }),
    data: data,
    actions: ['review', 'optimize'],
    category: 'efficiency'
  };
}

function createResourceNotification(data, t) {
  const resourceType = data.resource === 'water' ? t('resource_water') : t('resource_propane');
  
  return {
    type: NOTIFICATION_TYPES.RESOURCE_ALERT,
    priority: data.consumption > data.threshold * 1.3 ? PRIORITY_LEVELS.CRITICAL : PRIORITY_LEVELS.HIGH,
    title: data.resource === 'water' ? t('alert_high_water_consumption') : t('alert_high_propane_consumption'),
    message: t('resource_alert_message', {
      resource: resourceType,
      consumption: data.consumption,
      expected: data.threshold,
      increase: Math.round((data.consumption / data.threshold - 1) * 100)
    }),
    data: data,
    actions: ['check_equipment', 'optimize'],
    category: 'resources'
  };
}

function createProductionTargetNotification(data, t) {
  return {
    type: NOTIFICATION_TYPES.PRODUCTION_TARGET,
    priority: PRIORITY_LEVELS.INFO,
    title: t('alert_production_target_achieved'),
    message: t('production_target_message', {
      target: data.target,
      actual: data.actual,
      achievement: Math.round((data.actual / data.target) * 100)
    }),
    data: data,
    actions: ['celebrate', 'analyze'],
    category: 'achievement'
  };
}

function createMaintenanceNotification(data, t) {
  return {
    type: NOTIFICATION_TYPES.MAINTENANCE_REMINDER,
    priority: data.daysUntilDue <= 1 ? PRIORITY_LEVELS.HIGH : PRIORITY_LEVELS.MEDIUM,
    title: t('alert_maintenance_reminder'),
    message: t('maintenance_message', {
      equipment: data.equipment,
      days: data.daysUntilDue,
      type: data.maintenanceType
    }),
    data: data,
    actions: ['schedule', 'postpone'],
    category: 'maintenance'
  };
}

function createAnomalyNotification(data, t) {
  return {
    type: NOTIFICATION_TYPES.ANOMALY_DETECTED,
    priority: data.severity === 'critical' ? PRIORITY_LEVELS.CRITICAL : PRIORITY_LEVELS.HIGH,
    title: t('alert_anomaly_detected'),
    message: t('anomaly_message', {
      type: data.anomalyType,
      value: data.value,
      expected: data.expected
    }),
    data: data,
    actions: ['investigate', 'acknowledge'],
    category: 'anomaly'
  };
}

// Спрощені функції відправлення (заглушки)
function sendPushNotification(notification, t) {
  if ('Notification' in window && Notification.permission === 'granted') {
    new Notification(notification.title, {
      body: notification.message,
      icon: '/icon-192.png',
      tag: notification.id,
      requireInteraction: notification.priority === PRIORITY_LEVELS.CRITICAL
    });
  }
}

function sendEmailNotification(notification, recipients, t) {
  console.log('Would send email to:', recipients, 'with notification:', notification);
  // Тут буде реальна інтеграція з email сервісом
}

export default NotificationContext;